import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCheck, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FunnelAlertRunLog } from "@/components/admin/FunnelAlertRunLog";

type Row = {
  id: string;
  severity: string;
  title: string;
  body: string | null;
  read_at: string | null;
  created_at: string;
  escalation_level: number;
};

const fmt = (v: string) =>
  new Date(v).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/** Escalation desk: critical alerts + alert run log. Open to admins and the escalation role. */
const AdminEscalations = () => {
  const { user, loading: authLoading } = useAuth();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return setAllowed(false);
    Promise.all([
      supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }),
      supabase.rpc("has_role", { _user_id: user.id, _role: "escalation" }),
    ]).then(([a, e]) => setAllowed(a.data === true || e.data === true));
  }, [authLoading, user]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("admin_notifications")
      .select("id,severity,title,body,read_at,created_at,escalation_level")
      .contains("audience_roles", ["escalation"])
      .is("archived_at", null)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) toast.error(error.message);
    setRows((data as Row[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!allowed) return;
    void load();
    const ch = supabase
      .channel("escalations")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "admin_notifications" }, (p) => {
        const r = p.new as Row & { audience_roles?: string[] };
        if (!r.audience_roles?.includes("escalation")) return;
        setRows((prev) => (prev.some((x) => x.id === r.id) ? prev : [r, ...prev]));
        toast.error(r.title);
      })
      .subscribe();
    return () => void supabase.removeChannel(ch);
  }, [allowed, load]);

  const markRead = async (id: string) => {
    const stamp = new Date().toISOString();
    const { error } = await supabase
      .from("admin_notifications")
      .update({ read_at: stamp, read_by: user?.id ?? null })
      .eq("id", id);
    if (error) return toast.error(error.message);
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, read_at: stamp } : r)));
  };

  if (authLoading || allowed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" aria-hidden />
              Escalation access required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>This page is for admins and the escalation team.</p>
            <Button asChild>
              <Link to="/auth?next=%2Fadmin%2Fescalations">Sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const unread = rows.filter((r) => !r.read_at).length;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Escalations — Dr. Sully Speaks</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <main className="mx-auto max-w-5xl space-y-8 px-4 py-10">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground">Escalation desk</h1>
            <p className="text-sm text-muted-foreground">
              Critical funnel alerts and every alert check. {unread} unread.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden /> Refresh
          </Button>
        </header>

        <section aria-labelledby="crit" className="space-y-3">
          <h2 id="crit" className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden /> Critical alerts
          </h2>
          {rows.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No critical alerts yet. They appear here the moment a rule keeps failing.
              </CardContent>
            </Card>
          ) : (
            rows.map((r) => (
              <Card key={r.id} className={r.read_at ? "opacity-70" : "border-destructive/40"}>
                <CardContent className="flex flex-wrap items-start justify-between gap-3 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive">{r.severity}</Badge>
                      <span className="text-xs text-muted-foreground">{fmt(r.created_at)}</span>
                    </div>
                    <p className="font-medium text-foreground">{r.title}</p>
                    {r.body && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{r.body}</p>}
                  </div>
                  {!r.read_at && (
                    <Button size="sm" variant="ghost" onClick={() => void markRead(r.id)}>
                      <CheckCheck className="mr-1 h-4 w-4" aria-hidden /> Mark read
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </section>

        <FunnelAlertRunLog />
      </main>
    </div>
  );
};

export default AdminEscalations;
